'use client';

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Cookie } from "lucide-react";
import { useCookieConsent } from "@/context/CookieConsentContext";
import Link from "next/link";

export default function CookieBanner() {
    const { visible, consent, setConsent, hideBanner } = useCookieConsent();
    const [localConsent, setLocalConsent] = useState(consent);

    // При открытии баннера подтягиваем текущее согласие из контекста
    useEffect(() => {
        if (visible) {
            setLocalConsent(consent);
        }
    }, [visible, consent]);

    if (!visible) return null;

    const saveConsentAndHide = (newConsent: typeof consent) => {
        setConsent(newConsent); // обновляем контекст + localStorage
        hideBanner();
    };

    const handleAcceptAll = () => saveConsentAndHide({ functional: true, analytics: true });
    const handleSave = () => saveConsentAndHide(localConsent);
    const handleLater = () => {
        // обновляем контекст, чтобы текущее состояние галочек сохранилось
        saveConsentAndHide(localConsent);
    };

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
            <Card className="max-w-5xl mx-auto bg-gray-900 text-white border-gray-800 shadow-2xl">
                <CardContent className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                        <div className="flex-shrink-0">
                            <Cookie className="h-10 w-10 text-blue-400" />
                        </div>

                        <div className="flex-1">
                            <h3 className="text-lg font-semibold mb-2">Wir respektieren deine Privatsphäre</h3>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">
                                Wir verwenden Cookies, um die Plattform funktionsfähig zu machen und – mit deiner Zustimmung – das Nutzerverhalten anonym zu analysieren (Google Analytics mit IP-Anonymisierung).
                            </p>

                            <div className="space-y-3">
                                <label className="flex items-center gap-3 cursor-default">
                                    <input type="checkbox" checked disabled className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-400" />
                                    <span className="text-sm">Funktionale Cookies (immer aktiv – für Login, Sicherheit etc.)</span>
                                </label>

                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={localConsent.analytics}
                                        onChange={(e) => setLocalConsent({ ...localConsent, analytics: e.target.checked })}
                                        className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-400"
                                    />
                                    <span className="text-sm">Analyse-Cookies (Google Analytics – anonymisiert)</span>
                                </label>
                            </div>

                            <p className="text-xs text-gray-400 mt-4">
                                Mehr Infos in unserer{" "}
                                <Link href="/datenschutz" className="underline hover:text-blue-400 transition">
                                    Datenschutzerklärung
                                </Link>.
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 w-full md:w-auto">
                            <Button onClick={handleAcceptAll} className="bg-green-600 hover:bg-green-700 w-full">
                                Alle akzeptieren
                            </Button>
                            <Button onClick={handleSave} variant="secondary" className="w-full">
                                Auswahl speichern
                            </Button>
                            <Button onClick={handleLater} variant="ghost" className="w-full text-gray-400 hover:text-white">
                                Später entscheiden
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

